import { useEffect, useRef } from "react";
import gsap from "gsap";
import { formatInrParts } from "@/utils/format";

/**
 * Animates a number from `from` to `to` using GSAP.
 * Renders as Indian currency (Cr / L) via formatInrParts.
 */
export const CountUpInr = ({
  to,
  from = 0,
  duration = 1.6,
  delay = 0,
  className = "",
  unitClassName = "",
  testId,
}) => {
  const valueRef = useRef(null);
  const unitRef = useRef(null);

  useEffect(() => {
    const obj = { v: from };
    const ctx = gsap.context(() => {
      gsap.to(obj, {
        v: to,
        duration,
        delay,
        ease: "power3.out",
        onUpdate: () => {
          const parts = formatInrParts(obj.v);
          if (valueRef.current) valueRef.current.textContent = `₹ ${parts.value}`;
          if (unitRef.current) unitRef.current.textContent = parts.unit;
        },
      });
    });
    return () => ctx.revert();
  }, [to, from, duration, delay]);

  const initial = formatInrParts(from);

  return (
    <span data-testid={testId} className={className}>
      <span ref={valueRef}>₹ {initial.value}</span>
      {(initial.unit || to >= 100000) && (
        <span ref={unitRef} className={`${unitClassName} text-brand`}>
          {initial.unit}
        </span>
      )}
    </span>
  );
};

/**
 * Animates a raw integer (no Cr/L). Used for SIP monthly amount.
 */
export const CountUpRupees = ({
  to,
  from = 0,
  duration = 1.6,
  delay = 0,
  className = "",
  testId,
}) => {
  const ref = useRef(null);

  useEffect(() => {
    const obj = { v: from };
    const ctx = gsap.context(() => {
      gsap.to(obj, {
        v: to,
        duration,
        delay,
        ease: "power3.out",
        onUpdate: () => {
          if (ref.current) {
            ref.current.textContent = `₹ ${Math.round(obj.v).toLocaleString("en-IN")}`;
          }
        },
      });
    });
    return () => ctx.revert();
  }, [to, from, duration, delay]);

  return (
    <span ref={ref} data-testid={testId} className={className}>
      ₹ {Math.round(from).toLocaleString("en-IN")}
    </span>
  );
};
